import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Select } from "@amzn/stencil-react-components/select";
import { InputHeader } from "@amzn/stencil-react-components/input";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import React, { FC } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { FormField } from "@/common/components/FormField/form-field";
import { ComboBoxInput } from "@/common/components/ComboBoxInput/combo-box-input";
import { useAddressLocationOptions } from "@/common/hooks/use-address-location-options";
import type { PickupAddress } from "@/services/hardware-self-service/hardware-self-service.types";
import { isPhoneNumberValid, isPickupAddressComplete } from "../campaigns.util";

interface PickupAddressFormProps {
  address: PickupAddress;
  deviceNames: string[];
  onChange: (address: PickupAddress) => void;
}

export const PickupAddressForm: FC<PickupAddressFormProps> = ({
  address,
  deviceNames,
  onChange,
}) => {
  const { countries, phoneCodes, states, cities, isStatesLoading, isCitiesLoading } =
    useAddressLocationOptions(address.country, address.stateProvince);

  const updateField = (field: keyof PickupAddress) => (value: string) => {
    onChange({ ...address, [field]: value });
  };

  const handleCountryChange = (selectedCountryName: string) => {
    const next = countries.find((c) => c.name === selectedCountryName);
    onChange({
      ...address,
      country: selectedCountryName,
      countryCode: next?.phonecode ?? "",
    });
  };

  const isIncomplete = !isPickupAddressComplete(address);
  const hasUnusablePhoneNumber = !isIncomplete && !isPhoneNumberValid(address.phoneNumber);

  return (
    <View
      backgroundColor="neutral05"
      padding="S400"
      borderRadius="R200"
      border="1px solid var(--st-dt-color-neutral-20)"
    >
      <Col gridGap="S300">
        <Col gridGap="S100">
          <Text variant={TextVariant.HeadingSM} fontWeight="bold">
            {getString("campaigns.pickupAddress.heading")}
          </Text>
          <Text color="neutral70">
            {getString("campaigns.pickupAddress.description")}
          </Text>
          <Text color="neutral70" fontStyle="italic">
            {getString("campaigns.pickupAddress.appliesTo", {
              devices: deviceNames.join(", "),
            })}
          </Text>
        </Col>

        <Row gridGap="S300">
          <Col flex="1">
            <FormField
              testId="pickup-fullName"
              label={getString("campaigns.pickupAddress.fullName")}
              placeholder={getString("campaigns.pickupAddress.fullNamePlaceholder")}
              value={address.fullName}
              onChange={updateField("fullName")}
              required
            />
          </Col>
          <Col flex="1" gridGap="S200">
            <InputHeader
              labelText={getString("campaigns.pickupAddress.country")}
              htmlFor="pickup-country"
              required
            />
            <Select
              id="pickup-country"
              dataTestId="pickup-country"
              isSearchable
              isMulti={false}
              options={countries.map((c) => c.name)}
              value={address.country || undefined}
              onChange={(value) => handleCountryChange(value ?? "")}
            />
          </Col>
        </Row>

        <Row gridGap="S300">
          <Col gridGap="S100" flex="1">
            <ComboBoxInput
              label={getString("campaigns.pickupAddress.stateProvince")}
              testId="pickup-stateProvince"
              value={address.stateProvince}
              options={states}
              isLoading={isStatesLoading}
              required
              onChange={updateField("stateProvince")}
            />
          </Col>
          <Col gridGap="S100" flex="1">
            <ComboBoxInput
              label={getString("campaigns.pickupAddress.city")}
              testId="pickup-city"
              value={address.city}
              options={cities}
              isLoading={isCitiesLoading}
              required
              onChange={updateField("city")}
            />
          </Col>
        </Row>

        <Row gridGap="S300">
          <Col flex="1">
            <FormField
              testId="pickup-addressLine1"
              label={getString("campaigns.pickupAddress.addressLine1")}
              placeholder={getString("campaigns.pickupAddress.addressLine1Placeholder")}
              value={address.addressLine1}
              onChange={updateField("addressLine1")}
              required
            />
          </Col>
          <Col flex="1">
            <FormField
              testId="pickup-addressLine2"
              label={getString("campaigns.pickupAddress.addressLine2")}
              placeholder={getString("campaigns.pickupAddress.addressLine2Placeholder")}
              value={address.addressLine2}
              onChange={updateField("addressLine2")}
            />
          </Col>
        </Row>

        <Row gridGap="S300">
          <Col flex="1">
            <FormField
              testId="pickup-postalCode"
              label={getString("campaigns.pickupAddress.postalCode")}
              placeholder={getString("campaigns.pickupAddress.postalCodePlaceholder")}
              value={address.postalCode}
              onChange={updateField("postalCode")}
              required
            />
          </Col>
          <Col flex="1">
            <Row gridGap="S300">
              <Col gridGap="S200" width={120}>
                <InputHeader
                  labelText={getString("campaigns.pickupAddress.countryCode")}
                  htmlFor="pickup-countryCode"
                  required
                />
                <Select
                  id="pickup-countryCode"
                  dataTestId="pickup-countryCode"
                  isSearchable
                  isMulti={false}
                  placeholder="code"
                  options={phoneCodes}
                  value={address.countryCode ? `+ ${address.countryCode}` : undefined}
                  onChange={(value) => updateField("countryCode")(value?.replace(/^\+\s*/, "") ?? "")}
                />
              </Col>
              <Col flex="1">
                <FormField
                  testId="pickup-phoneNumber"
                  label={getString("campaigns.pickupAddress.phoneNumber")}
                  placeholder={getString("campaigns.pickupAddress.phoneNumberPlaceholder")}
                  value={address.phoneNumber}
                  onChange={updateField("phoneNumber")}
                  required
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {isIncomplete && (
          <MessageBanner type={MessageBannerType.Warning} isDismissible={false}>
            {getString("campaigns.pickupAddress.incompleteWarning")}
          </MessageBanner>
        )}

        {hasUnusablePhoneNumber && (
          <MessageBanner type={MessageBannerType.Warning} isDismissible={false}>
            {getString("campaigns.pickupAddress.invalidPhoneNumberWarning")}
          </MessageBanner>
        )}
      </Col>
    </View>
  );
};
